import { useState, useEffect, useRef } from 'react';
import { Bell, UserPlus, UserCheck, Eye, X, Check, MessageSquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import notificationService from '../../services/api/notification.service';
import { toast } from 'react-hot-toast';

// Global flag to prevent multiple components from fetching simultaneously
let globalFetchInProgress = false;
let globalLastFetchTime = 0;
const FETCH_COOLDOWN = 2000; // 2 seconds cooldown

const NotificationDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (!hasFetchedRef.current) {
      fetchUnreadCount();
      hasFetchedRef.current = true;
    }
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchUnreadCount = async () => {
    const now = Date.now();
    if (globalFetchInProgress || (now - globalLastFetchTime) < FETCH_COOLDOWN) {
      return;
    }

    try {
      globalFetchInProgress = true;
      globalLastFetchTime = now;
      const response = await notificationService.getUnreadCount();
      if (response.success) {
        setUnreadCount(response.data.unreadCount);
      }
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    } finally {
      globalFetchInProgress = false;
    }
  };

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await notificationService.getMyNotifications(1, 10);
      if (response.success) {
        setNotifications(response.data.notifications);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId, e) => {
    e.stopPropagation();
    try {
      await notificationService.markAsRead(notificationId);
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, isRead: true } : n
      ));
      setUnreadCount(Math.max(0, unreadCount - 1));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications(notifications.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      toast.error('Failed to mark all as read');
    }
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.isRead) {
      await handleMarkAsRead(notification.id, { stopPropagation: () => {} });
    }
    
    setIsOpen(false);
    
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'connection_request':
        return <UserPlus className="w-5 h-5 text-blue-600" />;
      case 'connection_accepted':
        return <UserCheck className="w-5 h-5 text-green-600" />;
      case 'new_follower':
        return <UserPlus className="w-5 h-5 text-purple-600" />;
      case 'portfolio_comment':
        return <MessageSquare className="w-5 h-5 text-purple-600" />;
      default:
        return <Bell className="w-5 h-5 text-gray-600" />;
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5 text-gray-600" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-xs font-semibold rounded-full ring-2 ring-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          {/* Mobile-only backdrop so the panel reads as an overlay, not a stray card */}
          <div
            className="fixed inset-0 bg-black/20 z-40 sm:hidden"
            onClick={() => setIsOpen(false)}
          />

          <div
            className="
              fixed left-2 right-2 top-16 z-50
              sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2
              w-auto sm:w-96
              max-w-[calc(100vw-1rem)] sm:max-w-none
              bg-white rounded-xl shadow-2xl border border-gray-200
              flex flex-col
              max-h-[calc(100vh-5rem)] sm:max-h-[32rem]
              overflow-hidden
            "
          >
            {/* Header — fixed, never scrolls away */}
            <div className="shrink-0 bg-[#1a2642] px-4 py-3 flex items-center justify-between">
              <h3 className="text-white font-semibold text-base sm:text-lg">Notifications</h3>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllAsRead}
                    className="text-xs text-white/90 hover:text-white flex items-center gap-1 px-2 py-1 rounded bg-white/20 hover:bg-white/30 transition-colors whitespace-nowrap"
                  >
                    <Check className="w-3 h-3" />
                    <span className="hidden xs:inline">Mark all read</span>
                    <span className="xs:hidden">Mark all</span>
                  </button>
                )}
                {/* Close button, mainly useful on mobile where there's no click-outside trigger area */}
                <button
                  onClick={() => setIsOpen(false)}
                  className="sm:hidden p-1 rounded hover:bg-white/20 transition-colors"
                  aria-label="Close notifications"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>

            {/* List — the ONLY scrollable section, takes remaining space */}
            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
              {loading ? (
                <div className="p-8 text-center">
                  <div className="inline-block w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
                  <p className="text-sm text-gray-500 mt-2">Loading...</p>
                </div>
              ) : notifications.length === 0 ? (
                <div className="p-8 text-center">
                  <Bell className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 font-medium">No notifications</p>
                  <p className="text-sm text-gray-400 mt-1">You're all caught up!</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      className={`p-3 sm:p-4 cursor-pointer transition-colors ${
                        notification.isRead 
                          ? 'bg-white hover:bg-gray-50' 
                          : 'bg-blue-50 hover:bg-blue-100'
                      }`}
                    >
                      <div className="flex gap-2.5 sm:gap-3">
                        <div className="flex-shrink-0 mt-1">
                          {notification.relatedUser?.profilePicture ? (
                            <img
                              src={notification.relatedUser.profilePicture}
                              alt=""
                              className="w-9 h-9 sm:w-10 sm:h-10 rounded-full object-cover border-2 border-gray-200"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div 
                            className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center shadow-md"
                            style={{ 
                              display: notification.relatedUser?.profilePicture ? 'none' : 'flex',
                              backgroundColor: '#1a2642'
                            }}
                          >
                            <Bell className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 mb-1 break-words">
                            {notification.title}
                          </p>
                          <p className="text-sm text-gray-600 line-clamp-2 break-words">
                            {notification.message}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {formatTime(notification.createdAt)}
                          </p>
                        </div>
                        {!notification.isRead && (
                          <button
                            onClick={(e) => handleMarkAsRead(notification.id, e)}
                            className="flex-shrink-0 p-1 hover:bg-white rounded transition-colors self-start"
                            title="Mark as read"
                          >
                            <Check className="w-4 h-4 text-blue-600" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer — fixed, always visible now regardless of screen height */}
            {notifications.length > 0 && (
              <div className="shrink-0 border-t border-gray-200 bg-gray-50 px-4 py-3">
                <button
                  onClick={() => {
                    setIsOpen(false);
                    navigate('/notifications');
                  }}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium w-full text-center"
                >
                  View all notifications
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationDropdown;